import * as Api from '@ViewModels';
import { format, isValid, parse, startOfToday } from 'date-fns';
import { parseAsString, parseAsStringLiteral, useQueryState } from 'nuqs';
import * as React from 'react';
import { useHistory } from 'react-router-dom';
import { useAccountContext } from '../../../admin/components/blogs/AccountContext';
import { useUserSession } from '../../../models/hooks/appStateHooks';
import { invalidateAllScheduledMeetingFilters } from '../../../queries';
import { BreadcrumbBar } from '../../components-v2/BreadcrumbBar';
import {
	AppDrawer,
	AppDrawerToolbar,
	AppDrawerToolbarButtonAiAssistant,
	StickyAppDrawerContent,
} from '../../components-v2/Drawer/AppDrawer';
import { Page } from '../../components-v2/PageLayout';
import { ActionItemDetailsFlyout } from '../../components/actionItems/ActionItemDetailsFlyout';
import { CalendarView } from '../../components/calendar/CalendarView';
import { CalendarToolbar } from '../../components/calendar/components/CalendarToolbar';
import {
	invalidateAllCalendarActionItems,
	useCalendarActionItems,
} from '../../components/calendar/hooks/useCalendarActionItems';
import { useCalendarEvents } from '../../components/calendar/hooks/useCalendarEvents';
import { useCalendarMeetings } from '../../components/calendar/hooks/useCalendarMeetings';
import { useCalendarUndatedActionItems } from '../../components/calendar/hooks/useCalendarUndatedActionItems';
import { styleSheet } from '../../components/calendar/styles';
import { CALENDAR_VIEW_MODES, CalendarItem } from '../../components/calendar/types';
import { useCalendarNavigation } from '../../components/calendar/useCalendarNavigation';
import {
	actionItemToCalendarItem,
	eventToCalendarItem,
	meetingToCalendarItem,
} from '../../components/calendar/utils/normalize';
import { MeetingDetailsFlyout } from '../../components/dataBoards/meetings/MeetingDetailsFlyout';
import { MultiContainerHeader } from '../../components/MultiContainerHeader';
import { DefaultUniversalAssistantChat } from '../../containers/templates/AssistantChat';

const DATE_FORMAT = 'yyyy-MM-dd';

/**
 * User-facing calendar for Levitate scheduled meetings, hosted events, and action items. Built on
 * the shared {@link CalendarView}; this page owns the data (host-filterable meetings, the org's
 * registration events, assigned action items), the rich toolbar (user picker + item-type legend),
 * and the detail flyouts. View + focused date are persisted to the URL so the page is shareable.
 */
export const Calendar: React.FC = () => {
	const userSession = useUserSession();
	const history = useHistory();
	const { impersonationContext } = useAccountContext();
	const eventsAvailable = !!userSession.account?.features?.surveys?.enabled;

	// --- User filter (one user at a time; defaults to "me") -----------------
	// Admins with scheduler reporting can search/pick any user; others stay on themselves.
	// Clearing the picker falls back to "me" rather than everyone.
	const currentUser = impersonationContext?.user ?? userSession.user ?? null;
	const [selectedUser, setSelectedUser] = React.useState<Api.IUser | null>(currentUser);
	const onUserChange = React.useCallback(
		(user: Api.IUser | null) => setSelectedUser(user ?? currentUser),
		[currentUser]
	);

	// --- Item-type visibility -----------------------------------------------
	const [showMeetings, setShowMeetings] = React.useState(true);
	const [showEvents, setShowEvents] = React.useState(true);
	const [showActionItems, setShowActionItems] = React.useState(true);

	// --- URL-synced nav state -----------------------------------------------
	const [viewMode, setViewMode] = useQueryState('view', parseAsStringLiteral(CALENDAR_VIEW_MODES).withDefault('month'));
	const [dateParam, setDateParam] = useQueryState(
		'date',
		parseAsString.withDefault(format(startOfToday(), DATE_FORMAT))
	);
	const focusedDate = React.useMemo(() => {
		const parsed = parse(dateParam, DATE_FORMAT, new Date());
		return isValid(parsed) ? parsed : startOfToday();
	}, [dateParam]);
	const nav = useCalendarNavigation({
		viewMode,
		onViewModeChange: setViewMode,
		focusedDate,
		onFocusedDateChange: date => setDateParam(format(date, DATE_FORMAT)),
	});

	// Roll overdue action items onto today only when today is actually in the visible window.
	const todayInWindow = React.useMemo(() => {
		const t = Date.now();
		return nav.range.start.getTime() <= t && t <= nav.range.end.getTime();
	}, [nav.range]);

	// --- Data ---------------------------------------------------------------
	const { meetings, isLoading: meetingsLoading } = useCalendarMeetings({
		range: nav.range,
		hostId: selectedUser?.id || undefined,
		impersonationContext,
	});
	const { events, isLoading: eventsLoading } = useCalendarEvents({
		impersonationContext,
		enabled: eventsAvailable,
	});
	const { actionItems, isLoading: actionItemsLoading } = useCalendarActionItems({
		range: nav.range,
		assignedToUserId: selectedUser?.id || undefined,
		impersonationContext,
	});
	// Undated action items have no place on the grid, so surface them on today (only when today is
	// visible). Fetched separately because the filter API can't select "no due date" items.
	const { undatedActionItems, hasMore: undatedHasMore } = useCalendarUndatedActionItems({
		assignedToUserId: selectedUser?.id || undefined,
		impersonationContext,
		enabled: showActionItems && todayInWindow,
	});

	// Merge the visible item types into one normalized list. Meetings are already window-scoped
	// server-side; events come back unfiltered, so we clip them to the range.
	const items = React.useMemo<CalendarItem[]>(() => {
		const list: CalendarItem[] = [];
		if (showMeetings) {
			for (const meeting of meetings) {
				const item = meetingToCalendarItem(meeting);
				if (item) {
					list.push(item);
				}
			}
		}
		if (showEvents && eventsAvailable) {
			for (const event of events) {
				const item = eventToCalendarItem(event);
				// Keep events that OVERLAP the window (not just start in it) so a multi-day
				// event still shows on day/week views whose window begins after it started.
				if (item && item.start <= nav.range.end && (item.end ?? item.start) >= nav.range.start) {
					list.push(item);
				}
			}
		}
		if (showActionItems) {
			for (const actionItem of actionItems) {
				const item = actionItemToCalendarItem(actionItem, { rollOverdueToToday: todayInWindow });
				if (item) {
					list.push(item);
				}
			}
			if (todayInWindow) {
				for (const actionItem of undatedActionItems) {
					const item = actionItemToCalendarItem(actionItem, { showUndatedOnToday: true });
					if (item) {
						list.push(item);
					}
				}
			}
		}
		return list;
	}, [
		meetings,
		events,
		actionItems,
		undatedActionItems,
		showMeetings,
		showEvents,
		showActionItems,
		eventsAvailable,
		todayInWindow,
		nav.range,
	]);

	// --- Item selection -----------------------------------------------------
	const [meetingIdToView, setMeetingIdToView] = React.useState<string | null>(null);
	const [isDetailsOpen, setIsDetailsOpen] = React.useState(false);
	const meetingToView = React.useMemo(
		() => (meetingIdToView ? (meetings.find(m => m.id === meetingIdToView) ?? null) : null),
		[meetingIdToView, meetings]
	);
	const [actionItemIdToView, setActionItemIdToView] = React.useState<string | null>(null);
	const [isActionItemOpen, setIsActionItemOpen] = React.useState(false);
	// Search both lists: undated items come from a separate query, not `actionItems`.
	const actionItemToView = React.useMemo(
		() =>
			actionItemIdToView
				? ([...actionItems, ...undatedActionItems].find(a => a.id === actionItemIdToView) ?? null)
				: null,
		[actionItemIdToView, actionItems, undatedActionItems]
	);
	// Reset each flyout's open flag if its selected item leaves the live list, so no stale open state lingers.
	React.useEffect(() => {
		if (!meetingToView) {
			setIsDetailsOpen(false);
		}
	}, [meetingToView]);
	React.useEffect(() => {
		if (!actionItemToView) {
			setIsActionItemOpen(false);
		}
	}, [actionItemToView]);
	const onSelectItem = React.useCallback(
		(item: CalendarItem) => {
			if (item.kind === 'meeting' && item.meeting) {
				setMeetingIdToView(item.meeting.id ?? null);
				setIsDetailsOpen(true);
			} else if (item.kind === 'event' && item.event?.id) {
				// Open the event for management (same destination as the Surveys list).
				history.push(`/surveys/event/edit/${item.event.id}`);
			} else if (item.kind === 'actionItem' && item.actionItem?.id) {
				setActionItemIdToView(item.actionItem.id);
				setIsActionItemOpen(true);
			}
		},
		[history]
	);

	// Scheduler permissions decide whether the flyout can mutate participant status
	// (mirrors the Meetings data board).
	const schedulerPermissions = impersonationContext?.account?.id
		? impersonationContext.user?.userGroupPermissions?.featurePermissions?.scheduler
		: userSession.featurePermissions?.scheduler;
	const canUpdateStatus = !!schedulerPermissions?.write;
	const canBrowseAll = !!schedulerPermissions?.viewReporting;

	const isLoading =
		meetingsLoading || (showEvents && eventsAvailable && eventsLoading) || (showActionItems && actionItemsLoading);

	return (
		// The AppDrawer portals into the app grid's drawer column, so it must be a sibling of
		// <Page> (not nested inside it) — matching the data boards and content calendar. Nested
		// inside <Page> the calendar rendered underneath the drawer instead of beside it.
		<>
			<Page styles={[styleSheet.page]}>
				<MultiContainerHeader appBarHeader={<BreadcrumbBar items={[{ label: 'My Calendar' }]} />} />

				<CalendarView
					nav={nav}
					items={items}
					isLoading={isLoading}
					onSelectItem={onSelectItem}
					undatedHasMore={undatedHasMore}
					renderToolbar={toolbarNav => (
						<CalendarToolbar
							viewMode={toolbarNav.viewMode}
							onViewModeChange={toolbarNav.onViewModeChange}
							title={toolbarNav.title}
							onPrevious={toolbarNav.onPrevious}
							onNext={toolbarNav.onNext}
							onToday={toolbarNav.onToday}
							currentUser={currentUser}
							selectedUser={selectedUser}
							onUserChange={onUserChange}
							canBrowseAll={canBrowseAll}
							showMeetings={showMeetings}
							onToggleMeetings={() => setShowMeetings(prev => !prev)}
							showEvents={showEvents}
							onToggleEvents={() => setShowEvents(prev => !prev)}
							eventsAvailable={eventsAvailable}
							showActionItems={showActionItems}
							onToggleActionItems={() => setShowActionItems(prev => !prev)}
						/>
					)}
				/>

				{meetingToView ? (
					<MeetingDetailsFlyout
						isOpen={isDetailsOpen}
						setIsOpen={setIsDetailsOpen}
						meeting={meetingToView}
						canUpdateStatus={canUpdateStatus}
						onParticipantStatusUpdated={() => invalidateAllScheduledMeetingFilters()}
					/>
				) : null}

				{actionItemToView ? (
					<ActionItemDetailsFlyout
						isOpen={isActionItemOpen}
						setIsOpen={setIsActionItemOpen}
						actionItem={actionItemToView}
						onChanged={() => invalidateAllCalendarActionItems()}
					/>
				) : null}
			</Page>

			{/* Right-hand AI assistant drawer, matching the data boards. It also hides the default FAB. */}
			<AppDrawer>
				<StickyAppDrawerContent value='ai-assistant'>
					<DefaultUniversalAssistantChat />
				</StickyAppDrawerContent>
				<AppDrawerToolbar>
					<AppDrawerToolbarButtonAiAssistant value='ai-assistant' />
				</AppDrawerToolbar>
			</AppDrawer>
		</>
	);
};
